/**
 * UsersScreen — directorio público de coleccionistas.
 *
 * Muestra la lista de users registrados con sus stats principales (% álbum,
 * cantidad de repetidas para intercambiar, ciudad, estrellas). Click en un
 * user → expande para ver su lista de repetidas con búsqueda. Acciones rápidas:
 * abrir chat, proponer trato.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../lib/Icon";
import { Avatar } from "../lib/components";
import { useCatalog } from "../contexts/CatalogContext";
import { fetchUsersDirectory, fetchUserDuplicates, type UserDirectoryRow } from "../api/users";
import { fetchUserReviews, type Review } from "../api/reviews";
import { ProposeTradeModal } from "./ProposeTradeModal";
import type { Route } from "../App";

interface Props {
  go: (r: Route) => void;
  openChat: (userId: number) => void;
}

export function UsersScreen({ go: _go, openChat }: Props) {
  const [users, setUsers] = useState<UserDirectoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [tradeWithUser, setTradeWithUser] = useState<UserDirectoryRow | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchUsersDirectory({ limit: 100 })
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter((u) =>
      u.name.toLowerCase().includes(q) ||
      (u.handle || "").toLowerCase().includes(q) ||
      (u.city || "").toLowerCase().includes(q) ||
      (u.country || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div className="chip" style={{ marginBottom: 6 }}>
          <Icon name="user" size={11} /> Coleccionistas
        </div>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
          Encontrá con quién tradear
        </h1>
        <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>
          Listado de coleccionistas registrados. Tocá uno para ver sus láminas repetidas.
        </p>
      </div>

      <div className="search-box" style={{ maxWidth: 480 }}>
        <Icon name="search" size={16} />
        <input
          placeholder="Buscar por nombre, handle, ciudad o país…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--fg-muted)" }}>
          Cargando coleccionistas…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--fg-muted)" }}>
          {search ? "Sin resultados" : "Aún no hay otros coleccionistas registrados."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {filtered.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              expanded={expandedId === u.id}
              onToggle={() => setExpandedId(expandedId === u.id ? null : u.id)}
              onChat={() => openChat(u.id)}
              onPropose={() => setTradeWithUser(u)}
            />
          ))}
        </div>
      )}

      {tradeWithUser && (
        <ProposeTradeModal
          toUserId={tradeWithUser.id}
          toUserName={tradeWithUser.name}
          onClose={() => setTradeWithUser(null)}
          onSent={() => setTradeWithUser(null)}
        />
      )}
    </div>
  );
}

// ============================================================================

interface UserCardProps {
  user: UserDirectoryRow;
  expanded: boolean;
  onToggle: () => void;
  onChat: () => void;
  onPropose: () => void;
}

function UserCard({ user, expanded, onToggle, onChat, onPropose }: UserCardProps) {
  const [dups, setDups] = useState<number[] | null>(null);
  const [dupsLoading, setDupsLoading] = useState(false);
  const [dupSearch, setDupSearch] = useState("");
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const { stickerById, teamById } = useCatalog();

  useEffect(() => {
    if (expanded && dups === null && !dupsLoading) {
      setDupsLoading(true);
      fetchUserDuplicates(user.id)
        .then(setDups)
        .catch(() => setDups([]))
        .finally(() => setDupsLoading(false));
      fetchUserReviews(user.id, 10).then(setReviews).catch(() => setReviews([]));
    }
  }, [expanded, user.id, dups, dupsLoading]);

  const filteredDups = useMemo(() => {
    if (!dups) return [];
    if (!dupSearch.trim()) return dups;
    const q = dupSearch.toLowerCase();
    return dups.filter((sid) => {
      const s = stickerById(sid);
      if (!s) return false;
      const t = teamById(s.team_id);
      return `${s.name} ${s.num} ${t?.name || ""}`.toLowerCase().includes(q);
    });
  }, [dups, dupSearch, stickerById, teamById]);

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      {/* Header — siempre visible */}
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          padding: 14,
          background: "transparent",
          border: 0,
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <Avatar user={user} size="lg" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{user.name}</span>
            {user.handle && <span className="tiny muted">{user.handle}</span>}
          </div>
          <div className="tiny muted" style={{ marginTop: 2 }}>
            {user.city ? `${user.city}, ${user.country}` : user.country}
            {" · "}Nivel {user.level}
            {user.stars_avg > 0 && (
              <> · <Icon name="star" size={11} /> {user.stars_avg.toFixed(1)}</>
            )}
            {user.trades_completed > 0 && (
              <> · {user.trades_completed} trueque{user.trades_completed === 1 ? "" : "s"}</>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <Stat label="Álbum" value={`${user.album_pct}%`} accent="var(--primary)" />
          <Stat
            label="Repes"
            value={user.dups_unique === user.dups_total
              ? user.dups_unique
              : `${user.dups_unique}/${user.dups_total}`}
            accent="var(--accent-amber)"
          />
          <span style={{ display: "inline-block", transform: expanded ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform 0.2s" }}>
            <Icon name="arrow" size={16} />
          </span>
        </div>
      </button>

      {/* Body expandido — repetidas + acciones */}
      {expanded && (
        <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-sm" onClick={onPropose}>
              <Icon name="hammer" size={12} /> Proponer trato
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onChat}>
              <Icon name="chat" size={12} /> Mensaje
            </button>
          </div>

          {dupsLoading ? (
            <div style={{ padding: 16, textAlign: "center", color: "var(--fg-muted)" }}>Cargando repetidas…</div>
          ) : !dups || dups.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", color: "var(--fg-muted)" }}>
              {user.name} no tiene láminas repetidas todavía.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div className="tiny muted" style={{ flex: 1 }}>
                  <b>{filteredDups.length}</b> {filteredDups.length === 1 ? "lámina repetida" : "láminas repetidas"} disponibles para intercambiar
                </div>
                <div className="search-box" style={{ maxWidth: 240 }}>
                  <Icon name="search" size={14} />
                  <input
                    placeholder="Filtrar repetidas…"
                    value={dupSearch}
                    onChange={(e) => setDupSearch(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-subtle)", textAlign: "left", position: "sticky", top: 0 }}>
                      <th style={cellHead}>#</th>
                      <th style={cellHead}>Lámina</th>
                      <th style={cellHead}>Equipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDups.slice(0, 200).map((sid) => {
                      const s = stickerById(sid);
                      const t = s ? teamById(s.team_id) : null;
                      return (
                        <tr key={sid} style={{ borderTop: "1px solid var(--border)" }}>
                          <td style={cell}>
                            <span style={{ width: 4, height: 22, display: "inline-block", background: t?.color || "#999", borderRadius: 2, verticalAlign: "middle", marginRight: 8 }} />
                            <b>{s?.num || "?"}</b>
                          </td>
                          <td style={cell}>{s?.name || "(eliminada)"}</td>
                          <td style={cell} className="tiny muted">{t?.name || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredDups.length > 200 && (
                <div className="tiny muted" style={{ textAlign: "center" }}>
                  Mostrando 200 de {filteredDups.length} — refiná con la búsqueda.
                </div>
              )}
            </>
          )}

          {/* Reseñas recibidas */}
          {reviews && reviews.length > 0 && (
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Icon name="star" size={14} />
                <b style={{ fontSize: 13 }}>Reseñas recibidas</b>
                <span className="tiny muted">({reviews.length})</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {reviews.map((r) => (
                  <div key={r.id} style={{ background: "var(--bg-subtle)", borderRadius: 8, padding: 10, fontSize: 13 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <Avatar user={r.reviewer} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{r.reviewer.name}</div>
                        <div className="tiny muted">{relativeDateUS(r.created_at)}</div>
                      </div>
                      <div style={{ color: "var(--accent-amber)", fontSize: 14, letterSpacing: 1 }}>
                        {"★".repeat(r.stars)}{"☆".repeat(5 - r.stars)}
                      </div>
                    </div>
                    {r.text && <div style={{ color: "var(--fg)", marginTop: 4 }}>{r.text}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {reviews && reviews.length === 0 && (
            <div className="tiny muted" style={{ borderTop: "1px solid var(--border)", paddingTop: 12, textAlign: "center" }}>
              Sin reseñas todavía. Sé el primero en intercambiar y dejar reseña.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function relativeDateUS(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `hace ${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}

// ============================================================================

function Stat({ label, value, accent }: { label: string; value: string | number; accent: string }) {
  return (
    <div style={{ textAlign: "right" }}>
      <div className="tiny muted">{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: accent, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

const cellHead: React.CSSProperties = {
  padding: "8px 10px",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--fg-muted)",
};

const cell: React.CSSProperties = { padding: "8px 10px", verticalAlign: "middle" };
